import { OperationStatus, ShippingPartner, ShippingType, TransportationOption } from "@deliverr/replenishment-client";
import { entries, find, isEmpty, isNil, keyBy, map, minBy, reduce, values } from "lodash";
import { ShipmentTransportationSelection, TransportationOptionSummary } from "./state/TransportationOptionsState";

export type TransportationOptionSummaries = Record<
  ShippingPartner.DELIVERR | ShippingPartner.FBA,
  {
    isLoading: boolean;
    isRetryLimitReached: boolean;
    isRetryOperationLoading: boolean;
    hasFetchFailed: boolean;
    transportationOptions: {
      [temporaryShipmentId: number]: Record<
        ShippingType.PARCEL | ShippingType.LTL,
        {
          isSupported: boolean;
          selectedOption?: TransportationOption;
          estimateOption?: TransportationOption;
          needsAlternateSelection: boolean;
        }
      >;
    };
  }
>;

const getCheapestCommonCarrierTransportationOption = (arrays: TransportationOption[][]): string | undefined => {
  if (isEmpty(arrays)) {
    return undefined;
  }

  // Create a map of how many times each unique item appears across arrays
  const itemMap: Record<string, { transportationOption: TransportationOption; count: number }> = {};

  for (const transportationOptions of arrays) {
    const seenInThisArray = new Set<string>();

    for (const transportationOption of transportationOptions) {
      const key = transportationOption.carrierName;

      if (!seenInThisArray.has(key)) {
        seenInThisArray.add(key);

        const entry = itemMap[key];

        if (entry) {
          entry.count += 1;
        } else {
          itemMap[key] = { transportationOption, count: 1 };
        }
      }
    }
  }

  // Filter to get items that appear in all arrays
  const commonCarrierTransportationOptions = values(itemMap).filter((entry) => entry.count === arrays.length);

  // return cheapest common carrier name
  return minBy(
    commonCarrierTransportationOptions,
    ({ transportationOption }) => transportationOption?.cost?.amount ?? 0
  )?.transportationOption?.carrierName;
};

const handleSelectionForFbaPartner = (
  selectedTransportationConfigurations: {
    [temporaryShipmentId: number]: Pick<ShipmentTransportationSelection, "shippingPartner" | "shippingType">;
  },
  fbaTransportationSummary?: TransportationOptionSummary
): ShipmentTransportationSelection[] => {
  const freightTransportationOptions = entries(selectedTransportationConfigurations).filter(
    ([_temporaryShipmentId, shipmentConfiguration]) =>
      shipmentConfiguration.shippingPartner === ShippingPartner.FBA &&
      shipmentConfiguration.shippingType === ShippingType.LTL
  );

  const commonFreightCarrierName = getCheapestCommonCarrierTransportationOption(
    freightTransportationOptions.map(
      ([temporaryShipmentId, _shipmentConfiguration]) =>
        fbaTransportationSummary?.transportationOptions?.[Number(temporaryShipmentId)]?.[ShippingType.LTL]
          ?.availableTransportationOptions ?? []
    )
  );

  const freightSelections = map(freightTransportationOptions, ([temporaryShipmentId, shipmentSelection]) => {
    const commonCarrierFreightOption = find(
      fbaTransportationSummary?.transportationOptions?.[Number(temporaryShipmentId)]?.[ShippingType.LTL]
        ?.availableTransportationOptions,
      (transportationOption) => transportationOption.carrierName === commonFreightCarrierName
    );

    return {
      temporaryShipmentId: Number(temporaryShipmentId),
      shippingPartner: shipmentSelection.shippingPartner,
      shippingType: shipmentSelection.shippingType,
      selectedTransportationOption: commonCarrierFreightOption,
      needsAlternateSelection: isNil(commonCarrierFreightOption),
    };
  });

  const parcelTransportationOptions = entries(selectedTransportationConfigurations).filter(
    ([_shipmentId, shipmentConfiguration]) =>
      shipmentConfiguration.shippingPartner === ShippingPartner.FBA &&
      shipmentConfiguration.shippingType === ShippingType.PARCEL
  );

  const commonParcelCarrierName = getCheapestCommonCarrierTransportationOption(
    parcelTransportationOptions.map(
      ([shipmentId]) =>
        fbaTransportationSummary?.transportationOptions?.[Number(shipmentId)]?.[ShippingType.PARCEL]
          ?.availableTransportationOptions ?? []
    )
  );

  const parcelSelections = map(parcelTransportationOptions, ([shipmentId, shipmentSelection]) => {
    const commonCarrierParcelOption = find(
      fbaTransportationSummary?.transportationOptions?.[Number(shipmentId)]?.[ShippingType.PARCEL]
        ?.availableTransportationOptions,
      (transportationOption) => transportationOption.carrierName === commonParcelCarrierName
    );

    return {
      temporaryShipmentId: Number(shipmentId),
      shippingPartner: shipmentSelection.shippingPartner,
      shippingType: shipmentSelection.shippingType,
      selectedTransportationOption: commonCarrierParcelOption,
      needsAlternateSelection: isNil(commonCarrierParcelOption),
    };
  });

  return [...freightSelections, ...parcelSelections];
};

const handleSelectionForFlexportPartner = (
  selectedTransportationConfigurations: {
    [temporaryShipmentId: number]: Pick<ShipmentTransportationSelection, "shippingPartner" | "shippingType">;
  },
  transportationSummary?: TransportationOptionSummary
): ShipmentTransportationSelection[] => {
  const flexportPartneredSelections = entries(selectedTransportationConfigurations).filter(
    ([_shipmentId, shipmentConfiguration]) =>
      shipmentConfiguration.shippingPartner === ShippingPartner.DELIVERR && !isNil(shipmentConfiguration.shippingType)
  );

  return map(flexportPartneredSelections, ([temporaryShipmentId, shipmentSelection]) => {
    const selectedTransportationOption =
      transportationSummary?.transportationOptions?.[Number(temporaryShipmentId)]?.[shipmentSelection.shippingType!]
        ?.preSelectedEstimate;

    return {
      temporaryShipmentId: Number(temporaryShipmentId),
      shippingPartner: shipmentSelection.shippingPartner,
      shippingType: shipmentSelection.shippingType,
      selectedTransportationOption,
      needsAlternateSelection: false,
    };
  });
};

export const getCheapestTransportationConfigurationsForSelection = (
  selectedTransportationConfigurations: {
    [temporaryShipmentId: number]: Pick<ShipmentTransportationSelection, "shippingPartner" | "shippingType">;
  },
  fbaTransportationSummary?: TransportationOptionSummary,
  flexportTransportationSummary?: TransportationOptionSummary
): {
  [temporaryShipmentId: string]: ShipmentTransportationSelection;
} => {
  const cheapestAmazonSelections = handleSelectionForFbaPartner(
    selectedTransportationConfigurations,
    fbaTransportationSummary
  );

  const cheapestDeliverrSelections = handleSelectionForFlexportPartner(
    selectedTransportationConfigurations,
    flexportTransportationSummary
  );

  const shipmentsWithNoTransportationSelected = entries(selectedTransportationConfigurations)
    .filter(([_temporaryShipmentId, shipmentConfiguration]) => isNil(shipmentConfiguration.shippingType))
    .map(([temporaryShipmentId, shipmentConfiguration]) => ({
      temporaryShipmentId: Number(temporaryShipmentId),
      shippingPartner: shipmentConfiguration.shippingPartner,
      selectedTransportationOption: undefined,
      needsAlternateSelection: false,
    }));

  return keyBy(
    [...cheapestAmazonSelections, ...cheapestDeliverrSelections, ...shipmentsWithNoTransportationSelected],
    (shipmentSelection) => shipmentSelection.temporaryShipmentId
  );
};

export const constructFBATransportationOptionSummaries = (
  areFlexportEstimatesLoading: boolean,
  hasFlexportRetryLimitReached: boolean,
  areFbaEstimatesLoading: boolean,
  hasFbaRetryLimitReached: boolean,
  isRetryOperationLoading: boolean,
  fbaEstimationSummary: TransportationOptionSummary | undefined,
  flexportEstimationSummary: TransportationOptionSummary | undefined,
  selectedTransportationConfigurations: {
    [temporaryShipmentId: number]: ShipmentTransportationSelection;
  }
): TransportationOptionSummaries => ({
  [ShippingPartner.DELIVERR]: {
    isLoading: areFlexportEstimatesLoading,
    isRetryLimitReached: hasFlexportRetryLimitReached,
    isRetryOperationLoading,
    hasFetchFailed:
      fbaEstimationSummary?.operationStatus === OperationStatus.FAILED ||
      flexportEstimationSummary?.operationStatus === OperationStatus.FAILED,
    transportationOptions: reduce(
      selectedTransportationConfigurations,
      (acc, shipmentSelection, temporaryShipmentId) => {
        const estimatedParcelOption =
          flexportEstimationSummary?.transportationOptions?.[temporaryShipmentId]?.[ShippingType.PARCEL]
            ?.preSelectedEstimate;

        const isParcelSupported =
          flexportEstimationSummary?.transportationOptions?.[temporaryShipmentId]?.[ShippingType.PARCEL]?.isSupported;

        const selectedParcelOption =
          shipmentSelection.shippingPartner === ShippingPartner.DELIVERR &&
          shipmentSelection.shippingType === ShippingType.PARCEL
            ? shipmentSelection.selectedTransportationOption
            : undefined;

        const estimatedFreightOption =
          flexportEstimationSummary?.transportationOptions?.[temporaryShipmentId]?.[ShippingType.LTL]
            ?.preSelectedEstimate;

        const selectedFreightOption =
          shipmentSelection.shippingPartner === ShippingPartner.DELIVERR &&
          shipmentSelection.shippingType === ShippingType.LTL
            ? shipmentSelection.selectedTransportationOption
            : undefined;

        const isFreightSupported =
          flexportEstimationSummary?.transportationOptions?.[temporaryShipmentId]?.[ShippingType.LTL]?.isSupported;

        acc[temporaryShipmentId] = {
          [ShippingType.PARCEL]: {
            isSupported: isParcelSupported,
            selectedOption: selectedParcelOption,
            estimateOption: estimatedParcelOption,
            needsAlternateSelection: false,
          },
          [ShippingType.LTL]: {
            isSupported: isFreightSupported,
            selectedOption: selectedFreightOption,
            estimateOption: estimatedFreightOption,
            needsAlternateSelection: false,
          },
        };
        return acc;
      },
      {}
    ),
  },
  [ShippingPartner.FBA]: {
    isLoading: areFbaEstimatesLoading,
    isRetryLimitReached: hasFbaRetryLimitReached,
    isRetryOperationLoading,
    hasFetchFailed: fbaEstimationSummary?.operationStatus === OperationStatus.FAILED,
    transportationOptions: reduce(
      selectedTransportationConfigurations,
      (acc, shipmentSelection, temporaryShipmentId) => {
        const estimatedParcelSummary =
          fbaEstimationSummary?.transportationOptions?.[Number(temporaryShipmentId)]?.[ShippingType.PARCEL];

        const selectedParcelOption =
          shipmentSelection.shippingPartner === ShippingPartner.FBA &&
          shipmentSelection.shippingType === ShippingType.PARCEL
            ? shipmentSelection.selectedTransportationOption
            : undefined;

        const estimatedFreightSummary =
          fbaEstimationSummary?.transportationOptions?.[Number(temporaryShipmentId)]?.[ShippingType.LTL];

        const selectedFreightOption =
          shipmentSelection.shippingPartner === ShippingPartner.FBA &&
          shipmentSelection.shippingType === ShippingType.LTL
            ? shipmentSelection.selectedTransportationOption
            : undefined;

        const needsAlternateParcelSelection =
          shipmentSelection.shippingPartner === ShippingPartner.FBA &&
          shipmentSelection.shippingType === ShippingType.PARCEL &&
          shipmentSelection.needsAlternateSelection;

        const needsAlternateFreightSelection =
          shipmentSelection.shippingPartner === ShippingPartner.FBA &&
          shipmentSelection.shippingType === ShippingType.LTL &&
          shipmentSelection.needsAlternateSelection;

        acc[temporaryShipmentId] = {
          [ShippingType.PARCEL]: {
            isSupported: estimatedParcelSummary?.isSupported,
            selectedOption: selectedParcelOption,
            estimateOption: estimatedParcelSummary?.preSelectedEstimate,
            needsAlternateSelection: needsAlternateParcelSelection,
          },
          [ShippingType.LTL]: {
            isSupported: estimatedFreightSummary?.isSupported,
            selectedOption: selectedFreightOption,
            estimateOption: estimatedFreightSummary?.preSelectedEstimate,
            needsAlternateSelection: needsAlternateFreightSelection,
          },
        };
        return acc;
      },
      {}
    ),
  },
});
